import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { NavLink, useParams } from "react-router-dom";

import { cn } from "~/components/ui/cn";
import { DollarCircleIcon } from "~/components/ui/icons/dollar-circle";
import { EuroCircleIcon } from "~/components/ui/icons/euro-circle";
import { PackageIcon } from "~/components/ui/icons/package";
import {
  Statistics,
  StatisticsContent,
  StatisticsTitle,
} from "~/components/ui/statistics";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import {
  StoreCurrency,
  StoreRegion,
  useStatsQuery,
} from "~/graphql/graphql.gen";
import type { BrandMatch } from "~/routes/root";
import { brandState } from "~/stores/brand";
import {
  activeSaleIdState,
  activeSaleState,
  isWaitingForSaleState,
} from "~/stores/sales";
import { formatNumber } from "~/utils/number";

export const HomeStats: React.FC = () => {
  const brand = useAtomValue(brandState);
  const { brandId = "" } = useParams<BrandMatch>();
  const { i18n, t } = useTranslation();

  const isWaitingForSale = useAtomValue(isWaitingForSaleState);
  const activeSale = useAtomValue(activeSaleState);
  const activeSaleId = useAtomValue(activeSaleIdState);

  const { data: statsData } = useStatsQuery(
    {
      saleId: activeSaleId ?? "",
    },
    {
      enabled: !!activeSaleId,
      refetchOnWindowFocus: false,
    },
  );

  if (isWaitingForSale) return null;

  const totalTurnover =
    brand?.store === StoreRegion.Fr
      ? (statsData?.statsV2.totalTurnoverTtc ?? 0) / 100
      : (statsData?.statsV2.totalTurnoverHt ?? 0) / 100;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between gap-2">
        <span className="text-xs text-gray-500">
          {t("home.overview_stats")}
        </span>
        <NavLink
          to={`/${brandId}/analytics`}
          className="text-xs font-semibold text-gray-700 hover:text-green-900"
        >
          {t("home.btn_more_stats")}
        </NavLink>
      </div>
      <div className="flex flex-col gap-4 sm:flex-row">
        <Statistics className="w-full">
          <StatisticsTitle>
            {activeSale?.currency === StoreCurrency.Usd ? (
              <DollarCircleIcon className="size-4 shrink-0" />
            ) : (
              <EuroCircleIcon className="size-4 shrink-0" />
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="border-b border-dashed border-gray-300 text-xs font-semibold uppercase leading-none tracking-wider text-gray-700 hover:border-solid">
                  {t("analytics.number_total_sale")}
                </TooltipTrigger>
                <TooltipContent side="top">
                  <div className="p-1">
                    {brand?.store === StoreRegion.Fr
                      ? t("analytics.tooltip_total_sale")
                      : t("analytics.tooltip_total_sale_ht")}
                  </div>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </StatisticsTitle>
          <StatisticsContent
            className={cn("gap-1", {
              "flex-row-reverse justify-end": i18n.language === "en",
            })}
          >
            <span className="text-4xl font-bold text-gray-900">
              {formatNumber(
                totalTurnover,
                i18n.language === "fr" ? "fr" : "en",
                {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                },
              )}
            </span>

            <span className="text-2xl font-bold text-gray-700">
              {activeSale?.currency === StoreCurrency.Usd ? "$" : "€"}
            </span>
          </StatisticsContent>
        </Statistics>
        <Statistics className="w-full">
          <StatisticsTitle>
            <PackageIcon className="size-4 shrink-0" />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="border-b border-dashed border-gray-300 text-xs font-semibold uppercase leading-none tracking-wider text-gray-700 hover:border-solid">
                  {t("analytics.number_total_orders")}
                </TooltipTrigger>
                <TooltipContent side="top">
                  <div className="p-1">
                    {t("analytics.tooltip_total_orders")}
                  </div>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </StatisticsTitle>
          <StatisticsContent className="gap-1">
            <span className="text-4xl font-bold text-gray-900">
              {statsData?.statsV2.orderCount ?? 0}
            </span>
          </StatisticsContent>
        </Statistics>
      </div>
    </section>
  );
};
